'use client';

import { create } from 'zustand';

interface AuthUser {
  id: string;
  name: string;
  username: string;
  boothId?: string;
}

interface AuthState {
  token: string | null;
  role: 'admin' | 'officer' | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  restoreSession: () => void;
}

const STORAGE_KEYS = {
  TOKEN: 'mpsec_token',
  ROLE: 'mpsec_role',
  USER: 'mpsec_user',
} as const;

// Try offline login by checking cached credentials in IndexedDB
async function tryOfflineLogin(
  username: string,
  password: string
): Promise<{ token: string; role: 'admin' | 'officer'; user: AuthUser } | null> {
  try {
    const { offlineDB } = await import('@/lib/offline-db');
    const { hashPassword } = await import('@/lib/auth');

    const cred = await offlineDB.getCredential(username);
    if (!cred) return null; // No cached credentials for this user

    // Verify password against stored hash
    const computedHash = await hashPassword(password);
    if (computedHash !== cred.passwordHash) return null; // Wrong password

    return {
      token: cred.token,
      role: cred.role,
      user: cred.user,
    };
  } catch (error) {
    console.error('Offline login failed:', error);
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  role: null,
  user: null,
  isAuthenticated: false,

  login: async (username: string, password: string): Promise<boolean> => {
    // Check if we're offline FIRST - skip API call entirely
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

    if (isOffline) {
      // Offline: try offline login immediately (no API call)
      const offlineResult = await tryOfflineLogin(username, password);
      if (offlineResult) {
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEYS.TOKEN, offlineResult.token);
          localStorage.setItem(STORAGE_KEYS.ROLE, offlineResult.role);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(offlineResult.user));
        }
        set({
          token: offlineResult.token,
          role: offlineResult.role,
          user: offlineResult.user,
          isAuthenticated: true,
        });
        return true;
      }
      return false;
    }

    try {
      // Online: try server login with a timeout (5 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.token && data.role && data.user) {
          // Save to localStorage for session persistence
          if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
            localStorage.setItem(STORAGE_KEYS.ROLE, data.role);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
          }

          // Save credentials to IndexedDB for offline login
          try {
            const { hashPassword } = await import('@/lib/auth');
            const passwordHash = await hashPassword(password);
            const { offlineDB } = await import('@/lib/offline-db');
            await offlineDB.saveCredential({
              username,
              passwordHash,
              role: data.role,
              user: data.user,
              token: data.token,
              savedAt: new Date().toISOString(),
            });
          } catch (e) {
            console.error('Failed to save offline credentials:', e);
          }

          set({
            token: data.token,
            role: data.role,
            user: data.user,
            isAuthenticated: true,
          });

          return true;
        }
        return false;
      }

      // Online auth failed (401, etc.) - try offline login
      const offlineResult = await tryOfflineLogin(username, password);
      if (offlineResult) {
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEYS.TOKEN, offlineResult.token);
          localStorage.setItem(STORAGE_KEYS.ROLE, offlineResult.role);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(offlineResult.user));
        }
        set({
          token: offlineResult.token,
          role: offlineResult.role,
          user: offlineResult.user,
          isAuthenticated: true,
        });
        return true;
      }
      return false;
    } catch (error) {
      // Network error / timeout - try offline login
      console.log('Network error/timeout, attempting offline login:', error);
      const offlineResult = await tryOfflineLogin(username, password);
      if (offlineResult) {
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEYS.TOKEN, offlineResult.token);
          localStorage.setItem(STORAGE_KEYS.ROLE, offlineResult.role);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(offlineResult.user));
        }
        set({
          token: offlineResult.token,
          role: offlineResult.role,
          user: offlineResult.user,
          isAuthenticated: true,
        });
        return true;
      }
      return false;
    }
  },

  logout: () => {
    // Clear session from localStorage but KEEP offline credentials in IndexedDB
    // so the user can log in offline again
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.ROLE);
      localStorage.removeItem(STORAGE_KEYS.USER);
    }

    set({
      token: null,
      role: null,
      user: null,
      isAuthenticated: false,
    });
  },

  restoreSession: () => {
    if (typeof window === 'undefined') return;

    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const role = localStorage.getItem(STORAGE_KEYS.ROLE) as 'admin' | 'officer' | null;
      const userStr = localStorage.getItem(STORAGE_KEYS.USER);

      if (token && role && userStr) {
        const user = JSON.parse(userStr) as AuthUser;
        set({
          token,
          role,
          user,
          isAuthenticated: true,
        });
      }
    } catch (error) {
      console.error('Failed to restore session:', error);
      // Clear corrupted data
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.ROLE);
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  },
}));
